import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import type { UndoAction } from '@/lib/history/types/undo-action'
import type { UndoAtomicAction } from '@/lib/history/types/undo-atomic-action'
import type { HistoryPersistencePlan } from '@/lib/history/types/history-persistence-plan'
import { isGroupShape } from '@/lib/groups/group-kind'
import type { AddGroupHistoryEntry } from '../types/add-group-history-entry'
import type { RemoveGroupHistoryEntry } from '../types/remove-group-history-entry'

/** Stable order for ungroup payloads: shapes by shape-list index, then text by text-list index. */
export function sortMixedGroupChildIds(
  ids: readonly string[],
  shapes: ShapeData[],
  textBoxes: TextBoxData[]
): string[] {
  const shapeIx = (id: string) => {
    const i = shapes.findIndex((s) => s.id === id)

    return i === -1 ? Infinity : i
  }
  const textIx = (id: string) => {
    const i = textBoxes.findIndex((t) => t.id === id)

    return i === -1 ? Infinity : i
  }

  return [...ids].sort((a, b) => {
    const as = shapeIx(a)
    const bs = shapeIx(b)

    if (as !== bs) return as - bs
    return textIx(a) - textIx(b)
  })
}

export function walkAtomicActions(
  action: UndoAction,
  visitor: (sub: UndoAtomicAction) => void
): void {
  if (action.type === 'compound') {
    for (const sub of action.actions) {
      visitor(sub)
    }
  } else {
    visitor(action as UndoAtomicAction)
  }
}

export function findAddGroupEntryInHistory(action: UndoAction): AddGroupHistoryEntry | undefined {
  let found: AddGroupHistoryEntry | undefined

  walkAtomicActions(action, (sub) => {
    if (sub.type === 'add' && isGroupShape(sub.shape)) {
      found = sub as AddGroupHistoryEntry
    }
  })

  return found
}

export function collectChildIdsReparentedToGroup(action: UndoAction, groupId: string): string[] {
  const ids = new Set<string>()

  walkAtomicActions(action, (sub) => {
    if (sub.type === 'update' && sub.after.parentGroupId === groupId) {
      ids.add(sub.shapeId)
    }

    if (sub.type === 'batchUpdate') {
      for (const item of sub.items) {
        if (item.after.parentGroupId === groupId) {
          ids.add(item.shapeId)
        }
      }
    }
  })

  return [...ids]
}

export function collectRemoveGroupEntries(action: UndoAction): RemoveGroupHistoryEntry[] {
  const out: RemoveGroupHistoryEntry[] = []

  walkAtomicActions(action, (sub) => {
    if (sub.type === 'remove' && isGroupShape(sub.shape)) {
      out.push(sub as RemoveGroupHistoryEntry)
    }
  })

  return out
}

export function resolveGroupChildMemberIds(
  action: UndoAction,
  plan: HistoryPersistencePlan,
  groupId: string
): string[] {
  const fromPlan = (plan.batchElementIds ?? []).filter((id) => id !== groupId)
  const fromHistory = collectChildIdsReparentedToGroup(action, groupId)
  const merged = new Set<string>()

  for (const id of fromPlan) merged.add(id)
  for (const id of fromHistory) merged.add(id)

  return [...merged]
}
